/**
 * Geofencing utilities for Habitia
 * Used for employee check-in/check-out proximity verification
 */

/**
 * Calculate distance between two GPS coordinates using the Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lng1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lng2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Earth's radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Check if a position is within a given radius of a target
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @param {number} targetLat - Target latitude (building)
 * @param {number} targetLng - Target longitude (building)
 * @param {number} radiusMeters - Allowed radius in meters (default: 50)
 * @returns {boolean}
 */
export const isWithinRadius = (userLat, userLng, targetLat, targetLng, radiusMeters = 50) => {
  const distance = calculateDistance(userLat, userLng, targetLat, targetLng);
  return distance <= radiusMeters;
};

/**
 * Get current position as a Promise wrapper around navigator.geolocation
 * @param {object} options - Geolocation options
 * @returns {Promise<{lat: number, lng: number, accuracy: number}>}
 */
export const getCurrentPosition = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocalizaci\u00F3n no soportada por este navegador"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error("Permiso de ubicaci\u00F3n denegado. Habilit\u00E1 la geolocalizaci\u00F3n en tu navegador."));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new Error("Ubicaci\u00F3n no disponible. Verific\u00E1 que el GPS est\u00E9 activado."));
            break;
          case error.TIMEOUT:
            reject(new Error("Tiempo de espera agotado para obtener la ubicaci\u00F3n."));
            break;
          default:
            reject(new Error("Error desconocido al obtener la ubicaci\u00F3n."));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
        ...options,
      }
    );
  });
};

/**
 * Watch position changes (for real-time tracking)
 * @param {function} onPosition - Callback with {lat, lng, accuracy}
 * @param {function} onError - Error callback
 * @returns {number} watchId - Use to clear with navigator.geolocation.clearWatch()
 */
export const watchPosition = (onPosition, onError) => {
  if (!navigator.geolocation) {
    onError?.(new Error("Geolocalizaci\u00F3n no soportada por este navegador"));
    return null;
  }

  return navigator.geolocation.watchPosition(
    (position) => {
      onPosition({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });
    },
    (error) => {
      onError?.(error);
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 10000,
    }
  );
};

/**
 * Format distance for display
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance
 */
export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};
